/**
 * "Send Message" — WhatsApp can go to one person or a whole audience; SMS/Email stay single-only.
 *
 * WhatsApp uses real, Meta-approved Cloud templates (the same ones Automations use), with a media
 * picker for templates whose header carries a file, plus a free-text option for replying within an
 * open 24h window (single-recipient only — free text can't be bulk-sent to a group).
 *
 * The "Audience" mode is a genuine bulk send: pick a saved audience instead of typing one phone
 * number, and it goes out to everyone in it right now. Under the hood this creates and immediately
 * runs a one-off Automation (see quickSendAutomation), so it gets the exact same guards, throttling,
 * media resolution and dedup as a scheduled campaign — nothing here duplicates that logic. Because
 * variables can't be typed once for hundreds of different people, audience sends require every
 * template variable to already be CRM-bound (or have a fallback) — the same requirement Automations
 * already enforce.
 *
 * SMS/Email still use the older, simpler MessageTemplate system (canned copy + token substitution) —
 * there's no Cloud-API equivalent for those channels, so nothing there needed to change.
 */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Wrench, Users, User, AlertTriangle } from 'lucide-react';
import { useSendMessageMutation, useListTemplatesQuery, useGetWhatsAppStatusQuery } from '../../api/communicationsApi';
import {
  useListWhatsAppTemplatesQuery, useSendWhatsAppTemplateMutation,
  useListAudiencesQuery, usePreviewAudienceQuery, useQuickSendAutomationMutation,
} from '../../api/campaignsApi';
import MediaPickerField from '../campaigns/MediaPickerField';
import { applyApiErrorsToForm } from '../../utils/apiError';
import ApiErrorBanner from '../../components/ApiErrorBanner';
import { toast } from 'react-toastify';

const FREE_TEXT = ''; // sentinel for the WhatsApp template <select>'s "free text" option
const MEDIA_KINDS = new Set(['Image', 'Video', 'Document']);

export default function SendMessageModal({ onClose }) {
  const { register, handleSubmit, watch, setValue, setError, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { channel: 'WhatsApp' },
  });
  const [send] = useSendMessageMutation();
  const [sendWaTemplate] = useSendWhatsAppTemplateMutation();
  const [quickSend, { isLoading: quickSending }] = useQuickSendAutomationMutation();
  const { data: templates = [] } = useListTemplatesQuery();
  const { data: waTemplates = [] } = useListWhatsAppTemplatesQuery({ usableOnly: true });
  const { data: waStatus } = useGetWhatsAppStatusQuery();
  const waPaused = !!waStatus?.paused;
  const channel = watch('channel');
  const templateId = watch('templateId');
  const matchingTemplates = templates.filter((t) => t.channel === channel);
  const [serverError, setServerError] = useState(null);

  // 'single' = today's phone/email flow. 'audience' = bulk, WhatsApp-only — see file header.
  const [recipientMode, setRecipientMode] = useState('single');
  const [audienceId, setAudienceId] = useState('');
  const { data: audiences = [] } = useListAudiencesQuery(undefined, { skip: channel !== 'WhatsApp' || recipientMode !== 'audience' });
  const { data: audiencePreview, isFetching: previewLoading } = usePreviewAudienceQuery(audienceId, { skip: !audienceId });

  const [waTemplateId, setWaTemplateId] = useState(FREE_TEXT);
  const [variables, setVariables] = useState({});
  const [mediaAssetId, setMediaAssetId] = useState(null);
  const selectedWaTemplate = waTemplates.find((t) => t.id === waTemplateId) ?? null;
  const mediaKind = selectedWaTemplate && MEDIA_KINDS.has(selectedWaTemplate.headerFormat)
    ? selectedWaTemplate.headerFormat : null;

  // Reset the WhatsApp-specific picks whenever the template or channel changes, so a stale
  // variable value or media file from a previous selection can't leak into a new send.
  useEffect(() => {
    setVariables({});
    setMediaAssetId(null);
  }, [waTemplateId, channel]);

  // Audience mode needs a real template — free text can't be sent to a group at once, and there's
  // no per-person value to type once for hundreds of different people either way.
  useEffect(() => {
    if (recipientMode === 'audience' && waTemplateId === FREE_TEXT && waTemplates.length > 0) {
      setWaTemplateId(waTemplates[0].id);
    }
  }, [recipientMode, waTemplateId, waTemplates]);

  // Switching back to single-recipient or away from WhatsApp always drops any audience pick.
  useEffect(() => {
    if (recipientMode !== 'audience' || channel !== 'WhatsApp') setAudienceId('');
  }, [recipientMode, channel]);

  // If WhatsApp is under maintenance, don't leave it as the pre-selected channel — steer staff
  // to a channel that will actually send.
  useEffect(() => {
    if (waPaused && channel === 'WhatsApp') setValue('channel', 'Sms');
  }, [waPaused, channel, setValue]);

  const setVariable = (name, value) => setVariables((v) => ({ ...v, [name]: value }));

  const submitQuickSend = async () => {
    if (mediaKind && !mediaAssetId && !selectedWaTemplate.defaultHeaderMediaAssetId) {
      setServerError('Choose a file for this template — its header requires one.');
      return;
    }
    try {
      const result = await quickSend({
        whatsAppTemplateId: waTemplateId,
        audienceId,
        headerMediaAssetId: mediaAssetId || undefined,
      }).unwrap();
      // The server claims the run and hands back a "Running" snapshot immediately — the actual
      // send continues in the background so a large audience isn't held up on this modal staying
      // open. Progress shows in the topbar's send indicator and in Campaigns → Automations, not
      // in a final count here.
      toast.success(`Started — sending to "${result.automationName}"'s audience in the background. Check the topbar or Automations for progress.`);
      onClose();
    } catch (e) {
      const msg = applyApiErrorsToForm(setError, e);
      setServerError(msg);
      toast.error(msg);
    }
  };

  const submitWhatsAppTemplate = async (data) => {
    if (mediaKind && !mediaAssetId && !selectedWaTemplate.defaultHeaderMediaAssetId) {
      setServerError('Choose a file for this template — its header requires one.');
      return;
    }
    try {
      const result = await sendWaTemplate({
        id: waTemplateId,
        recipient: data.recipient,
        variables,
        mediaAssetId: mediaAssetId || undefined,
      }).unwrap();
      if (result?.status === 'Failed') {
        const msg = result.failureReason || 'Message could not be sent.';
        setServerError(msg);
        toast.error(msg);
        return;
      }
      toast.success('Message sent');
      onClose();
    } catch (e) {
      const msg = applyApiErrorsToForm(setError, e);
      setServerError(msg);
      toast.error(msg);
    }
  };

  const submitFreeTextOrOtherChannel = async (data) => {
    try {
      const payload = {
        ...data,
        templateId: data.templateId || null,
        body: data.body || null,
        subject: data.subject || null,
      };
      const result = await send(payload).unwrap();
      // The API returns 200 even when the provider send itself failed (it's logged, not thrown) —
      // status must be checked explicitly or a failed WhatsApp send would show as "sent".
      if (result?.status === 'Failed') {
        const msg = result.failureReason || 'Message could not be sent.';
        setServerError(msg);
        toast.error(msg);
        return;
      }
      toast.success('Message sent');
      onClose();
    } catch (e) {
      const msg = applyApiErrorsToForm(setError, e);
      setServerError(msg);
      toast.error(msg);
    }
  };

  const submit = async (data) => {
    setServerError(null);
    if (data.channel === 'WhatsApp' && waPaused) {
      setServerError(waStatus.reason || 'WhatsApp sending is temporarily paused for maintenance.');
      return;
    }
    if (data.channel === 'WhatsApp' && recipientMode === 'audience') {
      await submitQuickSend();
    } else if (data.channel === 'WhatsApp' && waTemplateId !== FREE_TEXT) {
      await submitWhatsAppTemplate(data);
    } else {
      await submitFreeTextOrOtherChannel(data);
    }
  };

  // Single-recipient mode: a variable is missing only if nobody typed a value AND there's no fallback.
  const waFieldsMissing = selectedWaTemplate
    ? (selectedWaTemplate.variables ?? [])
      .filter((v) => !(variables[v.variableName] || '').trim() && !v.fallbackText)
      .map((v) => v.variableName)
    : [];

  // Audience mode: there is no per-send value to type, so a variable is only usable if the template
  // itself already resolves it — bound to a CRM field, or carrying a fallback. Mirrors the same
  // check AutomationModal makes before letting a campaign arm.
  const waUnboundForAudience = selectedWaTemplate
    ? (selectedWaTemplate.variables ?? [])
      .filter((v) => !v.bindingToken && !v.fallbackText)
      .map((v) => v.variableName)
    : [];

  const isAudienceSend = channel === 'WhatsApp' && recipientMode === 'audience';
  const submitDisabled = isSubmitting || quickSending
    || (channel === 'WhatsApp' && !isAudienceSend && selectedWaTemplate && waFieldsMissing.length > 0)
    || (isAudienceSend && (!audienceId || !selectedWaTemplate || waUnboundForAudience.length > 0
      || (mediaKind && !mediaAssetId && !selectedWaTemplate?.defaultHeaderMediaAssetId)));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit(submit)}>
          <div className="modal-header">
            <h2>Send Message</h2>
            <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
          <div className="modal-body">
            <ApiErrorBanner message={serverError} onDismiss={() => setServerError(null)} />
            {waPaused && (
              <div className="maintenance-banner" role="status">
                <Wrench size={16} />
                <span><strong>WhatsApp — Under Maintenance.</strong> Sending is temporarily paused; use SMS or Email instead.</span>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Channel *</label>
                <select className="form-control" {...register('channel')}>
                  <option value="WhatsApp" disabled={waPaused}>{waPaused ? 'WhatsApp (Under maintenance)' : 'WhatsApp'}</option>
                  <option value="Sms">SMS</option>
                  <option value="Email">Email</option>
                </select>
              </div>

              {channel === 'WhatsApp' && (
                <div className="form-group">
                  <label className="form-label">Send to</label>
                  <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--bg-subtle, var(--gray-50))', borderRadius: 'var(--r-md, 6px)' }}>
                    <button
                      type="button"
                      onClick={() => setRecipientMode('single')}
                      className={`btn btn-sm ${recipientMode === 'single' ? 'btn-primary' : 'btn-ghost'}`}
                      style={{ flex: 1 }}
                    >
                      <User size={13} /> One person
                    </button>
                    <button
                      type="button"
                      onClick={() => setRecipientMode('audience')}
                      className={`btn btn-sm ${recipientMode === 'audience' ? 'btn-primary' : 'btn-ghost'}`}
                      style={{ flex: 1 }}
                    >
                      <Users size={13} /> A group
                    </button>
                  </div>
                </div>
              )}
            </div>

            {channel === 'WhatsApp' && !isAudienceSend && (
              <div className="form-group">
                <label className="form-label">Template</label>
                <select className="form-control" value={waTemplateId} onChange={(e) => setWaTemplateId(e.target.value)}>
                  <option value={FREE_TEXT}>Free text (only within 24h of their last message)</option>
                  {waTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {waTemplates.length === 0 && (
                  <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 3 }}>
                    No approved, enabled templates yet — create or enable one under Communications → Templates.
                  </div>
                )}
              </div>
            )}

            {channel !== 'WhatsApp' && (
              <div className="form-group">
                <label className="form-label">Template (optional)</label>
                <select className="form-control" {...register('templateId')}>
                  <option value="">— None / free text —</option>
                  {matchingTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}

            {isAudienceSend ? (
              <>
                <div className="form-group">
                  <label className="form-label">Audience *</label>
                  <select className="form-control" value={audienceId} onChange={(e) => setAudienceId(e.target.value)}>
                    <option value="">Choose an audience…</option>
                    {audiences.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.kind})</option>)}
                  </select>
                  {audiences.length === 0 && (
                    <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 3 }}>
                      No saved audiences yet — build one under Campaigns → Audiences first.
                    </div>
                  )}
                  {audienceId && (
                    <div className="td-muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                      {previewLoading ? 'Counting…' : (
                        <>
                          <strong>{audiencePreview?.totalCount ?? 0}</strong> {audiencePreview?.capExceeded ? '(capped)' : ''} people currently match — this is who will be messaged.
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Template *</label>
                  <select className="form-control" value={waTemplateId} onChange={(e) => setWaTemplateId(e.target.value)}>
                    {waTemplates.length === 0 && <option value={FREE_TEXT}>No approved templates yet</option>}
                    {waTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>

                {selectedWaTemplate && (
                  <div className="td-muted" style={{ fontSize: '0.8rem', marginBottom: 10, whiteSpace: 'pre-wrap' }}>
                    {selectedWaTemplate.bodyText}
                  </div>
                )}

                {mediaKind && (
                  <div className="form-group">
                    <MediaPickerField kind={mediaKind} value={mediaAssetId} onChange={setMediaAssetId} />
                    {!mediaAssetId && selectedWaTemplate.defaultHeaderMediaAssetId && (
                      <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>
                        No file chosen — this template's default media will be used.
                      </div>
                    )}
                  </div>
                )}

                {selectedWaTemplate && (selectedWaTemplate.variables ?? []).length > 0 && (
                  <div className="td-muted" style={{ fontSize: '0.78rem', marginBottom: 10 }}>
                    Each recipient's values are filled in automatically:
                    <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                      {selectedWaTemplate.variables.map((v) => (
                        <li key={v.id}>
                          <span className="td-mono">{v.variableName}</span> → {v.bindingToken || (v.fallbackText ? `"${v.fallbackText}" (fixed)` : '—')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {waUnboundForAudience.length > 0 && (
                  <div className="td-muted" style={{ fontSize: '0.78rem', display: 'flex', gap: 6, color: 'var(--danger)', marginBottom: 8 }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                      These variables aren't linked to a CRM field yet: <strong>{waUnboundForAudience.join(', ')}</strong>.
                      Link them under Communications → Templates before sending to a group.
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="form-group">
                <label className="form-label">Recipient (phone or email) *</label>
                <input className="form-control" {...register('recipient', { required: 'Required' })} placeholder={channel === 'Email' ? 'patient@example.com' : '+91 9876543210'} />
                {errors.recipient && <span className="form-field-error">{errors.recipient.message}</span>}
              </div>
            )}

            {channel === 'WhatsApp' && !isAudienceSend && selectedWaTemplate ? (
              <>
                <div className="td-muted" style={{ fontSize: '0.8rem', marginBottom: 10, whiteSpace: 'pre-wrap' }}>
                  {selectedWaTemplate.bodyText}
                </div>

                {mediaKind && (
                  <div className="form-group">
                    <MediaPickerField kind={mediaKind} value={mediaAssetId} onChange={setMediaAssetId} />
                    {!mediaAssetId && selectedWaTemplate.defaultHeaderMediaAssetId && (
                      <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>
                        No file chosen — this template's default media will be used.
                      </div>
                    )}
                  </div>
                )}

                {(selectedWaTemplate.variables ?? []).map((v) => (
                  <div className="form-group" key={v.id}>
                    <label className="form-label">
                      {v.variableName} {!v.fallbackText && <span className="req">*</span>}
                    </label>
                    <input
                      className="form-control"
                      placeholder={v.fallbackText || v.exampleValue || ''}
                      value={variables[v.variableName] ?? ''}
                      onChange={(e) => setVariable(v.variableName, e.target.value)}
                    />
                    {v.fallbackText && !variables[v.variableName] && (
                      <div className="td-muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                        Leave blank to use the default: "{v.fallbackText}"
                      </div>
                    )}
                  </div>
                ))}
              </>
            ) : (!isAudienceSend && channel !== 'WhatsApp' && (
              <>
                {channel === 'Email' && (
                  <div className="form-group">
                    <label className="form-label">Subject {templateId ? '' : '*'}</label>
                    <input className="form-control" {...register('subject', { required: !templateId && channel === 'Email' ? 'Subject is required for email' : false })} />
                    {errors.subject && <span className="form-field-error">{errors.subject.message}</span>}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Body {templateId ? '(template will render; ignored if filled)' : '*'}</label>
                  <textarea className="form-control" rows={5} {...register('body', { required: templateId ? false : 'Required' })} />
                  {errors.body && <span className="form-field-error">{errors.body.message}</span>}
                </div>
              </>
            ))}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitDisabled}>
              {isSubmitting || quickSending ? 'Sending…' : isAudienceSend ? `Send to ${audiencePreview?.totalCount ?? '…'} people` : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
